import React, { createContext, useContext, useEffect, useState } from "react";
import useLazyScript from "hooks/useLazyScript";


interface PtcldScriptContextStore {
    scriptsLoaded: boolean;
    scriptError: string;
    loadingProgression: number;
    setLoadingProgression: React.Dispatch<React.SetStateAction<number>>;
    pointcloudLoading: boolean;
    setPointcloudLoading: React.Dispatch<React.SetStateAction<boolean>>;
    ifcLoading: boolean;
    setIfcLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isSplit: boolean;
    setIsSplit: React.Dispatch<React.SetStateAction<boolean>>;
    error: string;
    setError: React.Dispatch<React.SetStateAction<string>>;
    width: number;
    setWidth: React.Dispatch<React.SetStateAction<number>>;
    menu: boolean;
    setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isLock: boolean;
    setIsLock: React.Dispatch<React.SetStateAction<boolean>>;
    isOverlay: boolean;
    setIsOverlay: React.Dispatch<React.SetStateAction<boolean>>;
    setIsIfcAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    setIsObjAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    isIfcAvailable: boolean;
    isObjAvailable: boolean;
}
const initalStore: PtcldScriptContextStore = {
    scriptsLoaded: false,
    scriptError: "",
    loadingProgression: 0,
    setLoadingProgression: () => {},
    pointcloudLoading: false,
    setPointcloudLoading: () => {},
    ifcLoading: false,
    setIfcLoading: () => {},
    isSplit: false,
    setIsSplit: () => {},
    error: "",
    setError: () => {},
    width: 0,
    setWidth: () => {},
    menu: false,
    setMenuOpen: () => {},
    isLock: false,
    setIsLock: () => {},
    isOverlay: true,
    setIsOverlay: () => {},
    isIfcAvailable: false,
    isObjAvailable: false,
    setIsIfcAvailable: () => {},
    setIsObjAvailable: () => {},
};

const PtcldScriptContext = createContext<PtcldScriptContextStore>(initalStore);

export const PtcldScriptContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [scriptError, setScriptError] = useState("");
    const [pointcloudLoading, setPointcloudLoading] = useState(false);
    const [ifcLoading, setIfcLoading] = useState(false);
    const [isSplit, setIsSplit] = useState(false);
    const [error, setError] = useState("");
    const [loadingProgression, setLoadingProgression] = useState<number>(0);
    const [width, setWidth] = useState(0);
    const [menu, setMenuOpen] = useState(false);
    const [isLock, setIsLock] = useState(false);
    const [isOverlay, setIsOverlay] = useState(true);
    const [isIfcAvailable, setIsIfcAvailable] = useState(false);
    const [isObjAvailable, setIsObjAvailable] = useState(false);
    
    // Potree files
    const libsStylesLoaded = useLazyScript("/libs/core-libs/style.css", true, "link", setScriptError);
    const libsScriptLoaded = useLazyScript("/libs/core-libs/script.js", true, "script", setScriptError);
    const potreeStylesLoaded = useLazyScript("/libs/potree/potree.css", true, "link", setScriptError);
    const potreeScriptLoaded = useLazyScript(scriptLoaded ? "/libs/potree/potree.js" : "", true, "script", setScriptError);

    useEffect(() => {
        // if the styles and scripts are loaded then set the scriptLoaded to true
        if(libsStylesLoaded && potreeStylesLoaded && libsScriptLoaded){
            setScriptLoaded(true);
        }
    },  [libsStylesLoaded, potreeStylesLoaded, libsScriptLoaded, potreeScriptLoaded]);

    useEffect(() => {
        // whenever both scripts are loaded then set the scriptsLoaded to true
        // meaningless as scriptLoaded is true even when potreeScriptLoaded is false due to the previous useEffect
        if(scriptLoaded && potreeScriptLoaded){
            setScriptsLoaded(true);
        }
    }, [scriptLoaded, potreeScriptLoaded]);

    return (
        <PtcldScriptContext.Provider value={{
            scriptsLoaded,
            scriptError,
            loadingProgression,
            setLoadingProgression,
            pointcloudLoading,
            setPointcloudLoading,
            ifcLoading,
            setIfcLoading,
            isSplit,
            setIsSplit,
            error,
            setError,
            width,
            setWidth,
            menu,
            setMenuOpen,
            isLock,
            setIsLock,
            isOverlay,
            setIsOverlay,
            isIfcAvailable,
            isObjAvailable,
            setIsIfcAvailable,
            setIsObjAvailable,
        }}>
            {children}
        </PtcldScriptContext.Provider>
    );
};

export const usePtcldScriptContext = () => useContext(PtcldScriptContext);
    
